import apiRoutes from "@/src/apiRoutes";
import { Button } from "@/src/components/Button/Button";
import InputComponent from "@/src/components/Input/Input";
import { Modal } from "@/src/components/Modal/Modal";
import { textVariants } from "@/src/components/Text/Text";
import GoBack from "@/src/components/goBack";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/src/components/shad/card";
import { CheckEnablers } from "@/src/hooks/check-enablers";
import useUrlState from "@/src/hooks/use-url-state";
import api from "@/src/utils";
import { Switch } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const TFAEnabled = () => {
  const { t } = useTranslation();
  const trans = (key: string) =>
    t(
      `app.settings.securityCenter.verificationMethods.authApp.tfaEnabled.${key}`
    );
  const [modal, setModal] = useUrlState("modal");
  const [code, setCode] = useState("");
  const [action, setACtion] = useState("");
  const { data, refetch } = CheckEnablers("tfa");

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const enable = async () => {
    const res = await api.post(apiRoutes.tfa, {
      type: "enabler",
      action,
      code,
    });
    return res.data;
  };

  const { mutate, isLoading } = useMutation({
    mutationKey: ["enable_tfa"],
    mutationFn: enable,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        refetch();
        setModal(undefined);
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const activate = async () => {
    const res = await api.post(apiRoutes.tfa, {
      type: "activate",
      code,
    });
    return res.data;
  };

  const { mutate: deactivate, isLoading: deacLoad } = useMutation({
    mutationKey: ["activate_tfa"],
    mutationFn: activate,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        setModal(undefined);
        queryClient.invalidateQueries({ queryKey: ["check_tfa_enabled"] });
        navigate("/app/settings/security-center");
      } else {
        toast.warning(data?.info);
      }
    },
  });

  return (
    <>
      <GoBack route="/app/settings/security-center" />
      <Card>
        <CardHeader>
          <CardTitle>{trans("header")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 divide-y divide-gray-200">
          <div className="flex justify-between space-x-2 items-center py-3">
            <div className="flex flex-col">
              <span className={textVariants()}>
                {trans("body.withdrawals.title")}
              </span>
              <span className={textVariants({ size: "sm", variant: "desc" })}>
                {trans("body.withdrawals.desc")}
              </span>
            </div>
            <div>
              <Switch
                checked={data?.info?.withdrawal}
                onChange={() => [setModal("open"), setACtion("withdraw")]}
              />
            </div>
          </div>
          <div className="flex justify-between space-x-2 items-center py-3 ">
            <div className="flex flex-col">
              <span className={textVariants()}>
                {trans("body.login.title")}
              </span>
              <span className={textVariants({ size: "sm", variant: "desc" })}>
                {trans("body.login.desc")}
              </span>
            </div>
            <div>
              <Switch
                checked={data?.info?.login}
                onChange={() => [setModal("open"), setACtion("login")]}
              />
            </div>
          </div>
        </CardContent>
        <CardHeader className=" bg-back p-2 flex flex-row justify-between items-center border-t rounded-b-lg">
          <span className={textVariants({ variant: "desc", font: "medium" })}>
            {trans("footerText")}
          </span>

          <Button
            className="whitespace-nowrap"
            onClick={() => setModal("deactivate")}
            variant="warning"
          >
            {trans("disableButton")}
          </Button>
        </CardHeader>
      </Card>
      <Modal
        open={modal === "open"}
        setOpen={(val) => setModal(val ? undefined : "open")}
        title="Enable 2FA"
        // description="From the authenticator app you connected, you will find "
        footer={
          <div>
            <Button
              loading={isLoading}
              disabled={code.length < 6}
              variant={"outline"}
              onClick={() => mutate()}
            >
              Submit
            </Button>
          </div>
        }
        size={"sm"}
      >
        <div>
          <InputComponent
            onChange={(e) => setCode(e.target.value)}
            label="Enter the 2FA code from your authenticator app"
          />
        </div>
      </Modal>
      <Modal
        open={modal === "deactivate"}
        setOpen={(val) => setModal(val ? undefined : "deactivate")}
        title={trans("confirmModal.title")}
        // description="From the authenticator app you connected, you will find "
        footer={
          <div>
            <Button
              loading={deacLoad}
              disabled={code.length < 6}
              variant={"outline"}
              onClick={() => deactivate()}
            >
              {trans("confirmModal.form.submitButton")}
            </Button>
          </div>
        }
        size={"sm"}
      >
        <div>
          <InputComponent
            onChange={(e) => setCode(e.target.value)}
            label={trans("confirmModal.form.code.label")}
          />
        </div>
      </Modal>
    </>
  );
};

export default TFAEnabled;
